import { GPUInitializationError, Map, addProtocol, setWorkerUrl } from 'maplibre-gl'
import maplibreWorkerUrl from 'maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url'
import { useEffect, useMemo, useRef, useState, type RefObject } from 'react'
import type { Geometry } from 'geojson'
import type {
  ErrorEvent,
  GeoJSONSource,
  LngLatBoundsLike,
  LngLatLike,
  MapGeoJSONFeature,
  MapMouseEvent,
  MapSourceDataEvent,
} from 'maplibre-gl'
import {
  TASTING_CLUSTER_LAYER_ID,
  TASTING_POINT_LAYER_ID,
  TASTING_WINERY_BADGE_LAYER_ID,
  TASTING_WINERY_LAYER_ID,
  WISHLIST_POINT_LAYER_ID,
  addJournalLayers,
  placeJournalMarks,
  type NameAnchor,
  setJournalData,
  setJournalVisibility,
} from './journal_layers'
import { addPeakImage } from './peak_mark'
import {
  addRegionLayers,
  addSelectionLayers,
  regionInteractiveLayerIds,
  regionNameAt,
  regionSetting,
  regionTiers,
  resolveFeature,
  setRegionVisibility,
  setSelection,
  type SelectionFeature,
} from './region_layer'
import { shapeHolds } from './label_anchor'
import { RELIEF_PROTOCOL, addReliefWhenIdle, loadReliefTile, settleRelief } from './relief'
import { SLOT_TINT } from './slots'
import {
  addWineryLayers,
  setWinerySelection,
  setWineryVisibility,
  wineryInteractiveLayerIds,
  wineryObstacles,
} from './winery_layer'
import { ATLAS_STYLE, BASEMAP_SOURCE_ID, PEAK_IMAGE_ID, PLAIN_STYLE } from '../../lib/atlas_style'
import {
  NO_INSETS,
  boundsToFit,
  centreFor,
  fitPadding,
  flightMs,
  frameAt,
  inVisiblePart,
  openingCamera,
  rememberCamera,
  rememberedCamera,
  sameCamera,
  visibleOffset,
  type CameraPosition,
  type Insets,
  type PlaceCamera,
  type Size,
} from '../../lib/camera'
import {
  ATLAS_FILL_LAYER_ID,
  ATLAS_NAMED_LINE_LAYER_ID,
  ATLAS_RIBBON_LAYER_ID,
  ATLAS_SOURCE_ID,
  COUNTRY_EDGE_FADE_ZOOMS,
  COUNTRY_EDGE_MAX_ZOOM,
  COUNTRY_EDGE_OPACITY,
  COUNTRY_FILL_COLOR,
  COUNTRY_FILL_OPACITY,
  COUNTRY_FILL_OPACITY_DEEP,
  COUNTRY_FILL_OPACITY_NEAR,
  COUNTRY_HOVER_OPACITY_BOOST,
  COUNTRY_KIND,
  COUNTRY_LINE_COLOR,
  COUNTRY_NAMED_LINE_WIDTH_STOPS,
  COUNTRY_RECEDE_ZOOMS,
  COUNTRY_RIBBON_WIDTH_STOPS,
  COVERAGE_DRAWN,
  COVERAGE_NAMED,
  COVERAGE_NONE,
  FLIGHT_MAX_MS,
  FLIGHT_MIN_MS,
  MAP_FONT_PRIMED_STACKS,
  MAP_FONT_PRIMER,
  MARK_REACH_COARSE_PX,
  MARK_REACH_PX,
  MAX_ZOOM,
  NAMED_COUNTRY_FILL_COLOR,
  NAMED_COUNTRY_LINE_COLOR,
  NAMED_COUNTRY_LINE_DASH,
  PLACE_FLY_ZOOM,
  REGION_FILL_LAYER_PREFIX,
  REGION_LABEL_SOURCE_ID,
  REGION_SOURCE_ID,
  RELIEF_SOURCE_ID,
  UNCOVERED_COUNTRY_FILL_COLOR,
  WINERY_CLUSTER_LAYER_ID,
  WINERY_CLUSTER_ZOOM_STEP,
  WINERY_POINT_LAYER_ID,
  WINERY_SOURCE_ID,
} from '../../lib/map_config'
import type { HoverAbout } from '../../lib/hover'
import type { MapReach } from '../../lib/locate'
import type { AtlasLayer, DetailLayer, JournalLayer, MapViewport, WineryLayer } from '../../types/atlas'

// maplibre-gl v6 is ESM-only and has no default export, so the named imports
// above are the only form that works — `import maplibregl from 'maplibre-gl'`,
// the form in most tutorials, fails.
//
// Its worker ships as a separate ES module that imports a shared chunk and is
// resolved through `import.meta.url`, which a bundler graph does not answer
// reliably. So the URL is handed over, and this one call replaces that
// resolution.
//
// WHAT THE `?worker&url` IMPORT ABOVE YIELDS DEPENDS ON WHO ANSWERS IT. Left
// to Vite it is a second build: the worker bundled WITH everything it imports
// into one self-contained file, which made a first visit download MapLibre's
// shared code twice — once inside the library chunk and once, 144 KB gzipped,
// inside the worker. So in a build vite.config.ts answers it instead
// (`maplibreWorkerChunk`): the worker is a chunk of the same build as this
// file, it imports the `maplibre-shared` chunk the page has already fetched,
// and the value here is the worker chunk's URL. MapLibre starts any URL that
// does not end `.cjs` as a module worker, so nothing here says so. The dev
// server and Vitest still get Vite's own answer.
//
// It belongs in this module rather than in lib/map_config.ts because the
// layout imports map_config statically and only reaches this file through a
// dynamic import (KTD16). The bundled basemap style (lib/atlas_style.ts) is
// imported here for the same reason.
setWorkerUrl(maplibreWorkerUrl)

// The relief's tiles are asked for under a scheme of this app's own, so that
// each request goes through `loadReliefTile` — which knows which images exist
// and refuses the rest without asking (components/map/relief.ts). A protocol is
// registered with the library rather than with a map, which is why it is here
// beside the worker and not in the hook: once, when this chunk is evaluated.
// It costs nothing until a relief source exists, and none does until a map has
// drawn.
addProtocol(RELIEF_PROTOCOL, loadReliefTile)

// `unavailable` is the one member this hook never returns: it is set by the
// layout's `MapBoundary` when the dynamic import of the map fails, so the hook
// never runs at all. It lives in the same union because it is the same
// question — what state is the map in — and the layout switches on one value.
export type MapStatus = 'loading' | 'ready' | 'basemap-error' | 'unsupported' | 'unavailable'

export interface LayerVisibility {
  countries: boolean
  regions: boolean
  wineries: boolean
  tastings: boolean
  wishlist: boolean
}

// Where the camera goes for whatever an open panel describes. The type lives in
// lib/camera.ts with the arithmetic that reads it, because the layout builds one
// and must not import this file to do so (it is the map chunk). Re-exported so
// everything that already imports it from here keeps working.
export type { PlaceCamera }

/**
 * What the pointer is resting on that a click would OPEN: a place or a
 * producer, by slug. Reported so the shell can fetch its page before the click
 * (see `onIntent`). A cluster and a journal pin are not among them — a cluster
 * opens nothing, and a pin leaves the map.
 */
export interface HoverIntent {
  type: 'place' | 'winery'
  slug: string
}

type CallbackRef = RefObject<{
  insets: () => Insets
  onZoom: (zoom: number) => void
  onViewport: (viewport: MapViewport) => void
  onHover: (name: string | null, about?: HoverAbout) => void
  onIntent: (intent: HoverIntent | null) => void
  onDestination: (viewport: MapViewport) => void
  onSelect: (slug: string) => void
  onSelectTasting: (id: number) => void
  onSelectCluster: (slug: string) => void
  onSelectWinery: (slug: string) => void
}>

interface UseMaplibreOptions {
  container: RefObject<HTMLDivElement | null>
  /**
   * The country layer, or null until it arrives. It is a deferred prop: the
   * 300 KB of it used to be inlined in every atlas document, ahead of the
   * script that boots the app, and now follows in a request of its own. The map
   * is built without waiting for it and draws the countries when they land.
   */
  worldLayer: AtlasLayer | null
  countryMinZoom: number
  regionLayer: DetailLayer | null
  // R21. Plain props, so they arrive fresh after every save rather than being
  // cached (KTD15).
  tastingLayer: JournalLayer
  wishlistLayer: JournalLayer
  wineryLayer: WineryLayer | null
  /**
   * The zoom the winery layer starts drawing dots at (`map_zoom.winery_points`),
   * which is where a reader's wines from a winery become a mark on its dot
   * (journal_layers.ts). Optional: without it the marks use the constant that
   * mirrors the server's number.
   */
  wineryMarkZoom?: number
  layerVisibility: LayerVisibility
  selectedSlug: string | null
  selectedWinerySlug: string | null
  /**
   * The selected place's own outline, when the page has a finer one than the
   * map is holding. Optional: the shell passes `place.outline` for an open
   * place, and null for one still on its way or with no finer tier.
   *
   * The selection is normally drawn from the feature with that slug in the
   * region or world layer, which is the tier the camera's zoom was served —
   * nine vertices for Temecula at the overview tolerance. The place page can
   * ship that one place's detail geometry (`place.outline`); handed here, the
   * amber outline is drawn from it and the shard underneath stops mattering.
   */
  selectedOutline?: Geometry | null
  /**
   * Where to look. Read ONCE at construction — the map is built at this camera
   * rather than at the world view — and after that only when its `slug`
   * changes, which is an in-session move and animates.
   */
  camera: PlaceCamera | null
  /**
   * What the chrome is covering, asked for at the moment the camera moves. A
   * function rather than a value because the answer depends on the window as
   * well as on the panel, and the shell does not re-render on a resize.
   * Without one nothing is covered, which is what a test of the map alone has.
   */
  insets?: () => Insets
  // Fired once when the style has loaded and then whenever the integer zoom
  // changes.
  onZoom: (zoom: number) => void
  // Bounds and zoom together, fired once the style has loaded and on every
  // `moveend` after. ONE camera report feeds both viewport-scoped layers -- P4's
  // appellations and P6a's wineries -- because they watch the same camera; each
  // consumer in the shell decides what to do with it.
  onViewport: (viewport: MapViewport) => void
  // The name of whatever is under the pointer, or null — and, with a name,
  // what the tooltip says about it (lib/hover.ts): a second line that adds
  // something the map has not already lettered, whether it is an area, and
  // where its own name is on screen so the tooltip can keep off it. Fired only
  // when the name or that line CHANGES — it is a state update on the tooltip,
  // and a pointer crossing one appellation would otherwise be sixty of them a
  // second.
  onHover: (name: string | null, about?: HoverAbout) => void
  /**
   * Which openable thing the pointer is on, or null. Fired when that CHANGES,
   * never per pointer move — it rides the same bookkeeping as the hover
   * highlight, so crossing one appellation reports it once. Optional: the shell
   * uses it to fetch a page a reader is about to click, and a map with no shell
   * around it has nothing to fetch.
   */
  onIntent?: (intent: HoverIntent | null) => void
  /**
   * Where an in-session camera move is about to LAND, as the frame it will
   * settle in, fired as the move begins. The settled frame still arrives
   * through `onViewport` when it does; this is the head start, so whatever is
   * fetched per frame can be fetched while the camera is in the air. Not fired
   * for a move that does not happen (a producer already in plain view, or a
   * landing camera the map already has), nor for the camera the map was built
   * at.
   */
  onDestination?: (viewport: MapViewport) => void
  onSelect: (slug: string) => void
  onSelectTasting: (id: number) => void
  onSelectCluster: (slug: string) => void
  onSelectWinery: (slug: string) => void
  /**
   * Somewhere to leave the map itself, for as long as there is one. Optional,
   * and written to rather than read: the hook sets it when the map is built
   * and clears it when the map is removed. It is expected to be one object for
   * the life of the hook, as a ref is.
   *
   * Everything WebGL is otherwise behind this hook, and this is the one way
   * out, for the one feature that is not in this chunk: showing a reader where
   * they are (lib/locate.ts). Its source and its layers are fetched when
   * somebody asks for them, and code that arrives later needs a map to draw
   * on. Handing over the instance costs this chunk an assignment; drawing the
   * mark from in here would put all of it on every map page.
   *
   * WITH THE MAP GOES THE ONE WAY TO MOVE IT: `move`, which is `moveCamera`
   * with this hook's own insets and announcement. So a flight made from
   * outside lands beside the panel, takes the time every other flight takes,
   * stands still for a reader who asked for stillness and says where it is
   * going — and none of that arithmetic is written a second time, or pulled
   * into a second chunk.
   *
   * And two facts about how the map was built, which only this hook is in a
   * position to know (`MapReach` in lib/locate.ts): the camera it opened at,
   * and whether the tab remembered one.
   */
  mapHandle?: { current: MapReach | null }
}

// What the pointer is on. `source` and `id` address the feature whose paint
// reads the `hover` feature-state; a pin and a cluster have neither, because
// nothing about them is drawn differently under the pointer.
//
// `detail` is the tooltip's second line, as far as the feature alone can say
// it; `of` is which kind of thing it is, for the part only the map can add
// (an appellation's parent and how many producers are inside it — see
// `aboutHover`).
interface HoverTarget {
  name: string | null
  clickable: boolean
  source?: string
  id?: string
  detail?: string | null
  of?: 'region' | 'country'
}

// What the selection source was last handed, so an effect that re-runs for an
// unrelated reason — a pan merged more regions — does not push the same
// feature through the worker again.
interface ShownSelection {
  base: SelectionFeature | null
  outline: Geometry | null
  label: string | null
}

const NOTHING_SHOWN: ShownSelection = { base: null, outline: null, label: null }

// What the country source holds until the country layer arrives.
const NO_COUNTRIES: AtlasLayer = { type: 'FeatureCollection', features: [] }

const uncovered = () => NO_INSETS

const noIntent = () => {}
const noDestination = () => {}

/**
 * Owns the one imperative MapLibre instance. Everything WebGL is behind this
 * hook so the components around it can be tested under jsdom, which has no
 * WebGL at all and can therefore never render a real map.
 */
export default function useMaplibre({
  container,
  worldLayer,
  countryMinZoom,
  regionLayer,
  tastingLayer,
  wishlistLayer,
  wineryLayer,
  wineryMarkZoom,
  layerVisibility,
  selectedSlug,
  selectedWinerySlug,
  selectedOutline = null,
  camera,
  insets = uncovered,
  onZoom,
  onViewport,
  onHover,
  onIntent = noIntent,
  onDestination = noDestination,
  onSelect,
  onSelectTasting,
  onSelectCluster,
  onSelectWinery,
  mapHandle,
}: UseMaplibreOptions): MapStatus {
  const [status, setStatus] = useState<MapStatus>('loading')
  const mapRef = useRef<Map | null>(null)
  const styleLoaded = useRef(false)
  const shownSelection = useRef<ShownSelection>(NOTHING_SHOWN)

  // The camera the map is constructed at is read through a ref, like everything
  // else the construction effect needs: that effect depends on the container
  // alone, and a `camera` in its dependencies would rebuild the map on every
  // place the reader opened.
  const cameraRef = useRef(camera)
  cameraRef.current = camera
  // Which camera the map was BUILT showing, so the effect below does not then
  // fly to where it already is. Taken (and cleared) by that effect's next run.
  const openedAt = useRef<string | null>(null)

  const tiers = useMemo(() => (regionLayer ? regionTiers(regionLayer) : []), [regionLayer])

  // Everything a redraw needs is read through a ref rather than listed as an
  // effect dependency: a new prop object on every navigation would otherwise
  // tear the map down and build it again, which is the whole thing the
  // persistent layout exists to prevent.
  const drawRef = useRef({
    worldLayer,
    countryMinZoom,
    regionLayer,
    tastingLayer,
    wishlistLayer,
    wineryLayer,
    wineryMarkZoom,
    layerVisibility,
    selectedSlug,
    selectedWinerySlug,
    selectedOutline,
    tiers,
  })
  drawRef.current = {
    worldLayer,
    countryMinZoom,
    regionLayer,
    tastingLayer,
    wishlistLayer,
    wineryLayer,
    wineryMarkZoom,
    layerVisibility,
    selectedSlug,
    selectedWinerySlug,
    selectedOutline,
    tiers,
  }

  // Read through a ref by handlers that are registered once per style, so a
  // handler never calls a stale render's callback.
  const callbacks = {
    insets,
    onDestination,
    onHover,
    onIntent,
    onSelect,
    onSelectCluster,
    onSelectTasting,
    onSelectWinery,
    onViewport,
    onZoom,
  }
  const callbackRef = useRef(callbacks)
  callbackRef.current = callbacks

  useEffect(() => {
    const node = container.current
    if (!node) return

    // BUILT AT THE DESTINATION, not at the world view and flown in.
    //
    // A deep link to an appellation used to construct at zoom 1.3 and start a
    // flight: 3.5 to 4.2 s in the air, about 300 tile requests for ground the
    // reader never looks at, a wasted world-frame winery request, and nothing
    // asked of the place itself until the camera landed. Handing MapLibre the
    // camera in its constructor (it applies `bounds` with no animation) reaches
    // first idle in a few hundred milliseconds, and the first viewport report
    // — at `style.load`, below — is already the right frame.
    //
    // With no place to open, it is built where the reader last was in this tab
    // (lib/camera.ts), and only failing that at the world view.
    //
    // Whether the tab remembered anything is asked HERE, before the map
    // exists, because the map writes its camera the moment its style has
    // loaded and "nothing remembered" is false for ever after.
    const fresh = rememberedCamera() === null
    const { slug: openedFor, ...opening } = openingCamera(cameraRef.current, callbackRef.current.insets(), {
      width: node.clientWidth,
      height: node.clientHeight,
    })

    let map: Map
    try {
      map = new Map({
        container: node,
        // The atlas's own style, as an object in this chunk rather than a URL:
        // there is no style request left to wait for or to fail, and
        // `style.load` fires about as soon as the map exists.
        style: ATLAS_STYLE,
        ...opening,
        maxZoom: MAX_ZOOM,
        // The attribution is rendered in the layout chrome instead, where the
        // panel moves around it rather than covering it.
        attributionControl: false,
      })
    } catch (error) {
      // v6 removed WebGL1: WebGL2 is mandatory and the constructor throws rather
      // than degrading. Anything else is a real bug and is not swallowed.
      if (error instanceof GPUInitializationError) {
        setStatus('unsupported')
        return
      }
      throw error
    }

    mapRef.current = map
    if (mapHandle) {
      mapHandle.current = {
        map,
        move: (to) => moveCamera(map, to, callbackRef.current.insets(), callbackRef.current.onDestination),
        opened: positionOf(map),
        fresh,
      }
    }
    // For script/perf and for nothing else. The speed harness drives a real
    // browser and has to ask THIS instance what it has drawn and whether it is
    // still loading, and nothing in the DOM can answer either. `navigator.webdriver`
    // is true only in an automated browser, so a reader's window never holds it.
    if (navigator.webdriver) (window as unknown as { __atlasMap?: Map }).__atlasMap = map
    openedAt.current = openedFor
    styleLoaded.current = false
    shownSelection.current = NOTHING_SHOWN

    // Three separate facts about the basemap, because they fail differently:
    // `basemapFailed` is a failure of the basemap as a whole and is final for
    // this map; `tilesFailing` is "a tile failed and none has ever arrived",
    // which the next tile that does arrive takes back; `basemapDrew` is that
    // arrival.
    let basemapFailed = false
    let tilesFailing = false
    let basemapDrew = false

    // What the pointer is on. One per map, so it outlives a style swap.
    const hover = hoverState(map, callbackRef, drawRef)

    // How near a miss still counts as on a mark (`MARK_REACH_PX`). Asked once:
    // a reader does not change the kind of pointer they hold mid-visit.
    const reach = window.matchMedia?.('(pointer: coarse)').matches ? MARK_REACH_COARSE_PX : MARK_REACH_PX

    // The reader's counted marks, for the map as it stands (journal_layers.ts).
    // A visitor has no tastings, and this is then one length check.
    const placeMarks = () => replaceMarks(map, drawRef.current)

    // Bounds and zoom read at the same instant, so the pair always describes one
    // camera. `viewportOf` is what clamps them and widens an
    // antimeridian-crossing frame to the whole world, so every consumer sees a
    // frame its server can actually answer.
    //
    // Every report is also where the camera is REMEMBERED for the tab: one
    // short string, written when the camera comes to rest and never per frame.
    const reportViewport = () => {
      rememberCamera(positionOf(map))
      callbackRef.current.onViewport(viewportOf(map))
    }

    // Reported only when the integer zoom changes, not on every animation frame:
    // the shell reads this to cross one threshold and to enable one control, and
    // a state update per frame would re-render the chrome continuously.
    let reportedTier = Math.floor(map.getZoom())
    const reportZoom = () => {
      const zoom = map.getZoom()

      reportedTier = Math.floor(zoom)
      callbackRef.current.onZoom(zoom)
    }

    // Which style the map is on, counted: what lets something that was arranged
    // for one style find out it has been outlived by the next.
    let styleCount = 0

    map.on('style.load', () => {
      const draw = drawRef.current
      const style = ++styleCount

      styleLoaded.current = true
      // A style has just been built from nothing: no source, no feature-state.
      // Whatever the last one was showing has to be shown again, not skipped as
      // already there.
      shownSelection.current = NOTHING_SHOWN
      hover.forget()

      // The two font files: asked for here, SENT ONCE THIS HANDLER HAS RETURNED.
      // MapLibre reaches its fetch only after an `await`, and what follows an
      // `await` waits for the code that is running (this handler, and the
      // style load that fired it), so nothing leaves while the handler has the
      // thread and where in it the call stands changes nothing. It stands here
      // only to be read before the drawing. On every style, because a style
      // empties the glyph cache when it sets its `font-faces`.
      primeLettering(map)

      // The summit mark, before any tile has been parsed and could ask for it.
      addPeakImage(map)

      // Order is paint order only WITHIN a slot. The countries go under the
      // tint slot and the selection under its own; the appellations' marks are
      // inserted beneath them; the winery dots go under the dot slot, above all
      // of those and under the appellations' names; and the cluster chips and
      // the journal's pins are appended, which puts them above every slot — so
      // above any appellation tier, including one that arrives after this
      // handler has long since returned.
      drawAtlas(map, draw.worldLayer, draw.countryMinZoom)
      addSelectionLayers(map, null)
      if (draw.regionLayer) addRegionLayers(map, draw.regionLayer)
      // The layers are added even with no winery layer yet, so the source
      // exists before the first response lands and the arrival is a `setData`
      // rather than a restyle mid-pan.
      addWineryLayers(map, draw.wineryLayer, draw.selectedWinerySlug)
      // Added last, so the pins paint ABOVE the winery dots as well.
      const names = namesOn(map, draw)
      addJournalLayers(map, draw.tastingLayer, draw.wishlistLayer, chipsOf(draw), names, draw.wineryMarkZoom)
      applyVisibility(map, draw.tiers, draw.layerVisibility)
      showSelection(map, draw, shownSelection)
      setStatus(basemapFailed || tilesFailing ? 'basemap-error' : 'ready')

      // The FIRST viewport report, and the only one that can exist: MapLibre
      // sets the initial camera inside its constructor, before any listener is
      // registered, so `moveend` never fires for it. A reader who loads the map
      // and does not touch it would otherwise see the layer's toggle on and no
      // layer, with nothing on screen to explain it.
      //
      // The zoom is reported with it, and for the same reason: no `zoom` event
      // fires for a constructor camera either, and the shell starts out
      // believing the map is at the world view. A map built at zoom 11 on
      // Temecula would otherwise show its Regions toggle disabled, reading
      // "zoom in to show regions", over a screen full of them.
      reportZoom()
      reportViewport()

      // The relief, LAST and LATER: arranged here, added only once this style
      // has drawn and the map has come to rest. Nothing above waits for it, and
      // it is not in the style, so the first paint of a map with a relief asks
      // for exactly what the first paint of a map without one did.
      addReliefWhenIdle(map, () => mapRef.current === map && style === styleCount)
    })

    // The style names one image, the summit mark, and `style.load` registers it
    // before a tile can want it. Should a tile ever get there first, MapLibre
    // says so here instead of drawing the summit without its mark.
    map.on('styleimagemissing', (event?: { id?: string }) => {
      if (event?.id === PEAK_IMAGE_ID) addPeakImage(map)
    })

    // What a failure costs, by what failed.
    //
    // ONE TILE must not cost the basemap. This used to swap in the plain style
    // on any `error` at all and keep it for the life of the page, so a single
    // 5xx on a weak connection turned the atlas grey until a reload. MapLibre
    // already draws around a missing tile; all a tile error does here is raise
    // the notice, and only when no basemap tile has EVER arrived — which is the
    // tile server being down, where a reader is owed the explanation. The first
    // tile that does arrive takes the notice back (the `sourcedata` handler
    // below).
    //
    // Anything that is not a tile — the TileJSON failing, a style that will not
    // validate — is the basemap itself failing, and raises the notice for good.
    // Even then the style is only REPLACED when it never drew. Once the bundled
    // style has loaded it is already paper with the atlas on it, and swapping it
    // for the plain one would throw that away to draw the same thing.
    //
    // And NOTHING ABOUT THE RELIEF is the basemap failing, tile or not. It is
    // an extra laid under a map that has already drawn; an image of it that
    // will not load is a patch of flat paper, which is what the whole map was a
    // moment before. (A relief image that does not EXIST never gets this far:
    // it is refused without a request, and MapLibre takes a 404 in silence.)
    map.on('error', (event?: ErrorEvent & { tile?: unknown; sourceId?: string }) => {
      if (event?.sourceId === RELIEF_SOURCE_ID) return

      if (event?.tile) {
        const ours = event.sourceId !== undefined && event.sourceId !== BASEMAP_SOURCE_ID
        if (ours || basemapFailed || basemapDrew || tilesFailing) return

        tilesFailing = true
        setStatus('basemap-error')
        return
      }

      if (basemapFailed) return

      basemapFailed = true
      setStatus('basemap-error')

      if (styleLoaded.current) return

      // Nothing has drawn, so there is nothing to keep. The plain style carries
      // the same slots, and the `style.load` handler above redraws the atlas on
      // top of it.
      map.setStyle(PLAIN_STYLE)
    })

    map.on('sourcedata', (event: MapSourceDataEvent) => {
      if (event.sourceId !== BASEMAP_SOURCE_ID || !event.tile || basemapDrew) return

      basemapDrew = true
      if (!tilesFailing) return

      tilesFailing = false
      if (!basemapFailed) setStatus('ready')
    })

    // ONE pointer handler for the whole map, not one per layer.
    //
    // Each `map.on('mousemove', layers, fn)` runs its own
    // `queryRenderedFeatures` on every pointer move, and this file used to
    // register ten to fourteen of them. Worse, they fired in registration order
    // and the last `onHover` won: region tiers arrive after the winery layer,
    // so they were bound after it, and a winery dot inside an appellation was
    // named as the appellation. Here there is one query and the priority is
    // written down (`hoverTarget`).
    //
    // Registered on the map rather than per style, so a style swap cannot
    // double it. Synchronous on purpose: the name and the highlight are for the
    // frame the pointer is in.
    //
    // WHAT A POINTER MOVE COSTS: one `queryRenderedFeatures` at the pointer,
    // as it always has, and — only when that found no mark under it — a second
    // one over the marks' layers alone, in a small box round the pointer
    // (`hitsAt`). The second is what makes a dot's target bigger than the dot.
    map.on('mousemove', (event: MapMouseEvent) => {
      if (!styleLoaded.current) return

      hover.show(hoverTarget(hitsAt(map, event.point, drawRef.current.tiers, reach)))
    })

    // The pointer left the canvas altogether.
    map.on('mouseout', () => {
      if (styleLoaded.current) hover.show(null)
    })

    // ONE click handler too, and for a sharper reason than the pointer's.
    //
    // A click used to be handled per layer: one listener on the country fill,
    // one on each batch of appellation tiers as it arrived, one each on the
    // winery dots, the winery clusters, the tasting pins and their clusters.
    // MapLibre runs every listener with a feature under the pointer, and an
    // appellation is always drawn over its country — so one click on Temecula
    // Valley reported the United States and then Temecula Valley, and the
    // shell started two visits in the same tick. A guard kept a PIN from also
    // opening the country beneath it; nothing kept an appellation from doing
    // so, or a sub-appellation whose tier arrived in a later response from
    // also opening its container.
    //
    // Two visits looked harmless because the second interrupts the first. It
    // does not reliably: Inertia aborts a request through its XHR, and a visit
    // interrupted in the tick it was made has no XHR yet, so it is sent anyway
    // and its response is applied. The page that won was whichever answered
    // last. Once the appellation's page could come from memory, that was the
    // country every time — the click opened Temecula for one frame and then
    // the United States.
    //
    // So a click is resolved once, by the order the pointer is (`clickTarget`),
    // and reports one thing. Registered on the map rather than per style, so a
    // style swap cannot double it.
    map.on('click', (event: MapMouseEvent) => {
      if (!styleLoaded.current) return

      perform(map, clickTarget(hitsAt(map, event.point, drawRef.current.tiers, reach)), callbackRef)
    })

    map.on('zoom', () => {
      if (Math.floor(map.getZoom()) !== reportedTier) reportZoom()
    })

    // The settled frame, reported once the camera comes to rest rather than per
    // animation frame — `moveend` covers a pan and a zoom gesture alike, and it
    // is what KTD5's viewport fetch is keyed on. Separate from the zoom report
    // above because the two answer different questions: that one is "which
    // integer tier are we on", this one is "where is the camera now".
    //
    // Undebounced on purpose: each consumer in the shell debounces on its own
    // timer, so a hook-side debounce would only add a second delay in front of
    // theirs.
    map.on('moveend', () => {
      reportViewport()
      // The reader's counted marks are right for one zoom; at rest in another
      // they are worked out again. Nothing when there are no tastings, and no
      // write to the source when the marks come out the same.
      if (styleLoaded.current) placeMarks()
      // And the relief is as strong as the images under THIS ground allow
      // (relief.ts). A lookup in the manifest; a write only when the depth of
      // what is baked here differs from where the camera last rested.
      if (styleLoaded.current) settleRelief(map)
    })

    // <StrictMode> is on in entrypoints/inertia.tsx, so effects double-invoke.
    // Without this cleanup every mount leaves a second live map behind:
    // duplicated controls, doubled tile requests, stale handlers.
    return () => {
      mapRef.current = null
      if (mapHandle) mapHandle.current = null
      styleLoaded.current = false
      shownSelection.current = NOTHING_SHOWN
      map.remove()
    }
  }, [container])

  // New geometry goes into the existing source rather than rebuilding the map —
  // which is also how the countries are drawn on a hard load, where the layer
  // arrives after the style has: the source was added empty, and this fills it.
  // When it arrives BEFORE the style has loaded there is no source yet, and the
  // `style.load` handler draws it from `drawRef` instead.
  useEffect(() => {
    const source = mapRef.current?.getSource<GeoJSONSource>(ATLAS_SOURCE_ID)
    source?.setData(worldLayer ?? NO_COUNTRIES)
  }, [worldLayer])

  // The deferred appellation layer, added the first time it arrives. When it
  // arrives before the style has loaded, the style.load handler draws it instead.
  //
  // Every later arrival — the shell hands over the whole merged set after each
  // pan — goes through the same call, and `addRegionLayers` sends the source
  // only what changed.
  useEffect(() => {
    const map = mapRef.current
    if (!map || !styleLoaded.current || !regionLayer) return

    addRegionLayers(map, regionLayer)
    applyVisibility(map, regionTiers(regionLayer), drawRef.current.layerVisibility)
    // A place the reader has tasted in may just have been drawn, and its mark
    // hangs under its name (journal_layers.ts).
    replaceMarks(map, drawRef.current)
  }, [regionLayer])

  // The winery layer, whose data changes on every pan rather than arriving once.
  // `addWineryLayers` adds the source on first sight and updates it after, so
  // this one effect covers both without the caller tracking which it is.
  useEffect(() => {
    const map = mapRef.current
    if (!map || !styleLoaded.current) return

    // `addWineryLayers` is idempotent — it updates the source it already added
    // rather than throwing on the duplicate id, which is what makes it survive
    // a style swap as well as a pan.
    addWineryLayers(map, wineryLayer, drawRef.current.selectedWinerySlug)
    setWineryVisibility(map, drawRef.current.layerVisibility.wineries)
    // The chips have changed, and the reader's own counted marks keep clear of
    // them (journal_layers.ts).
    replaceMarks(map, drawRef.current)
  }, [wineryLayer])

  // An explicit toggle wins over the zoom rule: crossing the threshold still
  // fetches and adds the layers, but leaves a reader-hidden layer hidden. A
  // control that silently reverts is worse than no control.
  useEffect(() => {
    const map = mapRef.current
    if (!map) return

    applyVisibility(map, tiers, layerVisibility)
    // Chips that are switched off are nothing to keep clear of, and a name
    // that is switched off is nothing to hang under.
    if (styleLoaded.current) replaceMarks(map, drawRef.current)
  }, [tiers, layerVisibility])

  // The journal layers are plain props, so a save that adds a pin arrives as new
  // data on the next navigation rather than as a new map.
  useEffect(() => {
    const map = mapRef.current
    if (!map || !styleLoaded.current) return

    const draw = drawRef.current
    setJournalData(map, tastingLayer, wishlistLayer, chipsOf(draw), namesOn(map, draw), draw.wineryMarkZoom)
  }, [tastingLayer, wishlistLayer])

  // The place the open panel describes, so a deep-linked place is visibly the
  // one being described.
  //
  // It depends on the two layers as well as on the slug, and is declared after
  // the effects that draw them: the selection is a COPY of the feature with
  // that slug, and on a deep link that feature arrives after the slug does. It
  // depends on visibility because a place whose layer the reader has hidden is
  // not outlined over the gap where it was.
  useEffect(() => {
    const map = mapRef.current
    if (!map || !styleLoaded.current) return

    showSelection(map, drawRef.current, shownSelection)
  }, [selectedSlug, selectedOutline, regionLayer, worldLayer, layerVisibility])

  // The producer an open winery panel describes, so a deep link is visibly the
  // dot being described.
  useEffect(() => {
    const map = mapRef.current
    if (!map || !styleLoaded.current) return

    setWinerySelection(map, selectedWinerySlug)
  }, [selectedWinerySlug])

  // In-session camera moves: the reader opened something while the map was
  // already up, and is flown to it.
  //
  // Not the camera the map was BUILT at. That one is already on screen, and
  // moving to it again would be a flight from a place to itself — `openedAt` is
  // taken here so it is skipped exactly once, for exactly that slug, and a
  // later return to the same place is a move like any other: it flies, unless
  // the camera has not left in the meantime (`there`, in `moveCamera`).
  const cameraSlug = camera?.slug ?? null
  useEffect(() => {
    const map = mapRef.current
    const opened = openedAt.current
    openedAt.current = null

    if (!map || !camera || cameraSlug === opened) return

    moveCamera(map, camera, callbackRef.current.insets(), callbackRef.current.onDestination)
    // Keyed on the slug rather than on the object, so panning away from a place
    // and back to the same panel does not yank the camera.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraSlug])

  return status
}

// What the camera is looking at, clamped into a box the server can answer.
//
// MapLibre reports UNWRAPPED bounds: zoomed out the west edge can be -270, and a
// camera over the Pacific reports west past east because the frame crosses the
// antimeridian. `Geolocatable#intersecting_bbox` is four plain crossed float
// comparisons with nowhere to put a wrap flag, and `MapProps` answers west > east
// with an empty layer — so a wrapped frame is widened to the whole world here
// rather than fetched and silently drawn as nothing. Over-selecting costs
// payload; sending a frame that cannot be answered costs the map.
function viewportOf(map: Map): MapViewport {
  const bounds = map.getBounds()
  const west = bounds.getWest()
  const east = bounds.getEast()
  const wrapped = west > east || east - west >= 360

  return {
    west: wrapped ? -180 : Math.max(west, -180),
    south: Math.max(bounds.getSouth(), -90),
    east: wrapped ? 180 : Math.min(east, 180),
    north: Math.min(bounds.getNorth(), 90),
    zoom: map.getZoom(),
  }
}

// BOTH FONT FILES, ASKED FOR ONCE, BEFORE ANY TILE CAN ASK.
//
// MapLibre draws the map's lettering on this thread, from the two files the
// style declares (`font-faces`, lib/atlas_style.ts), and it fetches a file the
// first time a tile asks for a glyph of its stack. Its glyph cache is written
// only once the file has loaded and the rasteriser for it exists, so every
// tile that asks in the meantime waits on its own and then draws its own copy
// of every letter it wanted. A load that needs 90 to 97 distinct glyphs drew
// 177 to 670 of them, at about a third of a millisecond each, on the thread
// that everything between `style.load` and a drawn map is queued for.
//
// So the map asks first: one glyph of each stack, as soon as a style has
// loaded, about a tenth of a second before the first tile asks for any. That
// fetches both files (from the HTTP cache), hands them to the browser and
// builds both rasterisers. A tile that asks after that is answered inside its
// own task, and the cache then de-duplicates as it was written to. A tile that
// asks BEFORE the files are ready still draws its own copies, as all of them
// did: this makes that window shorter and takes nothing away.
//
// Nothing a reader sees changes. It is the same rasteriser drawing the same
// glyphs from the same files, and the letter asked for here is drawn once in
// each face and kept, like any other.
//
// `style.glyphManager` is a field of MapLibre's, not part of its API, so:
// - it is read as MapLibre's own types declare it, with no cast, so an
//   upgrade that moves or reshapes it fails `tsc` on this function (and on
//   the test in use_maplibre.test.tsx that says so in words);
// - it is looked for before it is called, and whatever it throws or rejects
//   with is dropped. Without it every label is still lettered, exactly as
//   before this existed; only the duplicate draws come back.
function primeLettering(map: Map) {
  const glyphs = map.style?.glyphManager
  if (typeof glyphs?.getGlyphs !== 'function') return

  try {
    const asked = glyphs.getGlyphs(Object.fromEntries(MAP_FONT_PRIMED_STACKS.map((stack) => [stack, [MAP_FONT_PRIMER]])))

    // `Promise.resolve`, so that an answer which is not a promise is not an
    // exception either.
    void Promise.resolve(asked).catch(() => {})
  } catch {
    // The tiles ask for themselves, as they always have.
  }
}

// One value per coverage state, as a `match` on the feature's own property.
//
// Three states, not a boolean: boundaries-drawn, names-only, and nothing-yet. P1
// had a boolean because outside the United States the honest answer was always
// no; P5 created the third state, where a country's appellations are in the
// atlas and in the search box and no open source publishes a shape for any of
// them.
//
// `none` is spelled out rather than left to the fallback alone: the fallback is
// the same value, so a fourth coverage state added server-side still draws, but
// every state this build knows about is named here rather than implied — the
// expression array is cast `as never`, so nothing else would flag a typo.
function byCoverage<Value>(values: Record<string, Value>) {
  return [
    'match',
    ['get', 'coverage'],
    COVERAGE_DRAWN,
    values[COVERAGE_DRAWN],
    COVERAGE_NAMED,
    values[COVERAGE_NAMED],
    COVERAGE_NONE,
    values[COVERAGE_NONE],
    values[COVERAGE_NONE],
  ]
}

const hovered = ['boolean', ['feature-state', 'hover'], false]
const zoomRamp = (...stops: unknown[]) => ['interpolate', ['linear'], ['zoom'], ...stops]

// A line inset by half its own width: its outer edge is the polygon's.
const inset = (stops: number[]) => stops.map((value, index) => (index % 2 === 0 ? value : value / 2))

function drawAtlas(map: Map, worldLayer: AtlasLayer | null, countryMinZoom: number) {
  // A style swap discards the previous style's sources, so this normally runs
  // once per style — but addSource throws on a duplicate id, and a style reload
  // must not take the map down.
  if (map.getSource(ATLAS_SOURCE_ID)) return

  // `promoteId` takes the feature id from the slug already in the properties, so
  // no feature has to carry a duplicate id column across the wire — and it is
  // the id the `hover` feature-state is set on.
  //
  // Added even when the layer has not arrived, empty: the source and its three
  // layers then exist in their place in the stack, and the arrival is a
  // `setData` rather than a late `addLayer` under everything drawn since.
  map.addSource(ATLAS_SOURCE_ID, { type: 'geojson', data: worldLayer ?? NO_COUNTRIES, promoteId: 'slug' })

  // THREE STATES, ONE INK (`COUNTRY_FILL_COLOR` in map_config.ts): a drawn
  // country is a tint and a solid ribbon inside its coast, a names-only country
  // a fainter tint and a dashed line, and a country with nothing is bare
  // ground — which now means relief.
  //
  // Under the tint slot: over the land, under the water. The country polygons
  // are simplified to about 22 km, and drawn under the water layer it is the
  // surveyed coastline that becomes their edge, not theirs.
  //
  // EVERY country is in this layer, the bare ones at an opacity of nothing, and
  // at every zoom: it is what a click and the pointer are tested against, and a
  // country with no appellations still opens a panel that says so. The opacity
  // is where the states differ, and where the whole layer recedes as the
  // appellations take over (see `COUNTRY_FILL_OPACITY`). Selection is not in
  // this expression: it is a source of its own (region_layer.ts), so choosing a
  // country repaints nothing here.
  map.addLayer(
    {
      id: ATLAS_FILL_LAYER_ID,
      type: 'fill',
      source: ATLAS_SOURCE_ID,
      minzoom: countryMinZoom,
      paint: {
        'fill-color': byCoverage({
          [COVERAGE_DRAWN]: COUNTRY_FILL_COLOR,
          [COVERAGE_NAMED]: NAMED_COUNTRY_FILL_COLOR,
          [COVERAGE_NONE]: UNCOVERED_COUNTRY_FILL_COLOR,
        }) as never,
        'fill-opacity': zoomRamp(
          COUNTRY_RECEDE_ZOOMS[0],
          ['+', byCoverage(COUNTRY_FILL_OPACITY), ['case', hovered, COUNTRY_HOVER_OPACITY_BOOST, 0]],
          // A drawn country is gone by here: its appellations are what says it,
          // and with no film on an appellation its tint would be the only film
          // left on the page.
          COUNTRY_RECEDE_ZOOMS[1],
          byCoverage(COUNTRY_FILL_OPACITY_NEAR),
          COUNTRY_RECEDE_ZOOMS[2],
          byCoverage(COUNTRY_FILL_OPACITY_DEEP),
        ) as never,
        'fill-antialias': false,
      },
    },
    SLOT_TINT,
  )

  // The edge marks, under the tint slot too: these are the coarse outlines, and
  // they belong under the water for the same reason the fill does — the sea
  // trims the side of a ribbon that a 22 km simplification pushed off the
  // coast. Both stop where that simplification would start to show
  // (`COUNTRY_EDGE_MAX_ZOOM`), which is also where the basemap's own border
  // takes over.
  const edge = (coverage: string) =>
    zoomRamp(COUNTRY_EDGE_FADE_ZOOMS[0], COUNTRY_EDGE_OPACITY[coverage], COUNTRY_EDGE_FADE_ZOOMS[1], 0)

  // Drawn: a solid ribbon inside the coast.
  map.addLayer(
    {
      id: ATLAS_RIBBON_LAYER_ID,
      type: 'line',
      source: ATLAS_SOURCE_ID,
      minzoom: countryMinZoom,
      maxzoom: COUNTRY_EDGE_MAX_ZOOM,
      filter: ['==', ['get', 'coverage'], COVERAGE_DRAWN] as never,
      layout: { 'line-join': 'round' },
      paint: {
        'line-color': COUNTRY_LINE_COLOR,
        'line-width': zoomRamp(...COUNTRY_RIBBON_WIDTH_STOPS) as never,
        'line-offset': zoomRamp(...inset(COUNTRY_RIBBON_WIDTH_STOPS)) as never,
        'line-opacity': edge(COVERAGE_DRAWN) as never,
      },
    },
    SLOT_TINT,
  )

  // Held as names: a dashed line. The dash, not the tint, is what separates it
  // from shaded relief, which is umber and has no edge.
  map.addLayer(
    {
      id: ATLAS_NAMED_LINE_LAYER_ID,
      type: 'line',
      source: ATLAS_SOURCE_ID,
      minzoom: countryMinZoom,
      maxzoom: COUNTRY_EDGE_MAX_ZOOM,
      filter: ['==', ['get', 'coverage'], COVERAGE_NAMED] as never,
      layout: { 'line-join': 'round' },
      paint: {
        'line-color': NAMED_COUNTRY_LINE_COLOR,
        'line-width': zoomRamp(...COUNTRY_NAMED_LINE_WIDTH_STOPS) as never,
        'line-offset': zoomRamp(...inset(COUNTRY_NAMED_LINE_WIDTH_STOPS)) as never,
        'line-dasharray': NAMED_COUNTRY_LINE_DASH,
        'line-opacity': edge(COVERAGE_NAMED) as never,
      },
    },
    SLOT_TINT,
  )
}

/**
 * What a click is ON, and therefore the one thing it does.
 *
 * The same order the pointer resolves in (`hoverTarget`), deliberately: naming
 * Margaux under the pointer and then opening Bordeaux on the click reads as a
 * broken map, and so does a pointer cursor over something a click then ignores.
 * Top of the stack first — the reader's mark on a winery, a tasting pin or a
 * cluster of them, a winery dot or a cluster of them, the deepest appellation,
 * the country.
 *
 * - The reader's MARK ON A WINERY opens that winery, whose panel lists the
 *   wines (`wineryMarkHit` has the one exception, under its badge). So does a
 *   counted mark that is one winery's wines. Neither opens the journal. (A
 *   mark of two wineries that no zoom tells apart is not one winery's: it is
 *   their place's, below, and says which in journal_layers.ts.)
 * - A single tasting pin opens its tasting. A COUNTED MARK of them that stands
 *   for one place opens the journal filtered to that place rather than zooming
 *   in, which is the one thing that would not work: the tastings in it are
 *   frequently on the IDENTICAL point — every wine that resolves to its
 *   country lands on that country's one representative point — so zooming
 *   forever never splits them, and the tastings inside would be unreachable
 *   from the map. A mark that stands for SEVERAL places is only crowded, and
 *   eases the camera in as a winery chip does: closer in it comes apart.
 * - A winery dot reports its slug upward and the shell navigates. A winery
 *   CLUSTER eases the camera in instead; navigating on one would be picking
 *   one of its members at random.
 * - An appellation goes through `resolveFeature`, because European
 *   appellations nest and one pointer position is regularly four of them
 *   (KTD13).
 * - A wishlist pin is passed through to whatever is beneath it. It has never
 *   answered a click of its own.
 *
 * A pure function of the hits, so the order is testable without a map.
 */
export type ClickTarget =
  | { kind: 'tasting'; id: number }
  | { kind: 'tasting-cluster'; slug: string | null; winery: string | null; center: [number, number] }
  | { kind: 'winery'; slug: string }
  | { kind: 'winery-cluster'; center: [number, number] }
  | { kind: 'place'; slug: string }

export function clickTarget(hits: readonly MapGeoJSONFeature[]): ClickTarget | null {
  const on = (layerId: string) => hits.find((hit) => hit.layer.id === layerId)

  const marked = wineryMarkHit(hits)
  if (marked) {
    const slug = named(marked.neighbour ? marked.neighbour.properties?.slug : marked.mark.properties?.winery_slug)

    return slug ? { kind: 'winery', slug } : null
  }

  const tasting = on(TASTING_POINT_LAYER_ID)
  if (tasting) {
    const id = tasting.properties?.id

    return id === undefined ? null : { kind: 'tasting', id: Number(id) }
  }

  // A counted mark says which place it stands for, when it stands for one
  // (journal_layers.ts) — it used to be a MapLibre cluster, which carried only
  // an id, and the place had to be asked of the worker.
  const pins = on(TASTING_CLUSTER_LAYER_ID)
  if (pins) {
    const geometry = pins.geometry

    return geometry.type === 'Point'
      ? {
          kind: 'tasting-cluster',
          slug: named(pins.properties?.place_slug),
          winery: named(pins.properties?.winery_slug),
          center: [geometry.coordinates[0], geometry.coordinates[1]],
        }
      : null
  }

  const winery = on(WINERY_POINT_LAYER_ID)
  if (winery) {
    const slug = named(winery.properties?.slug)

    return slug ? { kind: 'winery', slug } : null
  }

  const cell = on(WINERY_CLUSTER_LAYER_ID)
  if (cell) {
    const geometry = cell.geometry

    return geometry.type === 'Point'
      ? { kind: 'winery-cluster', center: [geometry.coordinates[0], geometry.coordinates[1]] }
      : null
  }

  const region = resolveFeature(hits.filter((hit) => hit.layer.id.startsWith(`${REGION_FILL_LAYER_PREFIX}-`)))
  const place = named((region ?? on(ATLAS_FILL_LAYER_ID))?.properties?.slug)

  return place ? { kind: 'place', slug: place } : null
}

/**
 * What a hit on the reader's mark on a winery is ON, for the click and the
 * pointer alike, so the name under the pointer is what a click opens.
 *
 * The mark is two discs (journal_layers.ts). Its DOT is on the winery's own
 * point and is that winery, whatever else is near. Its BADGE stands a few
 * pixels up and to the right of the dot, and where wineries are dense that is
 * where the next one is: the badge is 15 px across and neighbouring dots are 8
 * to 12 px apart. So a hit on the badge alone that is also on ANOTHER winery's
 * dot is that other winery (`neighbour`): the reader aimed at a dot. Without
 * this a neighbour under the badge could not be opened at all.
 */
function wineryMarkHit(hits: readonly MapGeoJSONFeature[]) {
  const on = (layerId: string) => hits.find((hit) => hit.layer.id === layerId)

  const dot = on(TASTING_WINERY_LAYER_ID)
  if (dot) return { mark: dot, neighbour: undefined }

  const badge = on(TASTING_WINERY_BADGE_LAYER_ID)
  if (!badge) return null

  const own = named(badge.properties?.winery_slug)
  const neighbour = hits.find((hit) => hit.layer.id === WINERY_POINT_LAYER_ID && named(hit.properties?.slug) !== own)

  return { mark: badge, neighbour }
}

// Reports upward rather than navigating here, because the shell is what knows
// whether a panel is already open and therefore whether to replace.
function perform(map: Map, target: ClickTarget | null, callbacks: CallbackRef) {
  if (!target) return

  if (target.kind === 'tasting') {
    callbacks.current.onSelectTasting(target.id)
  } else if (target.kind === 'winery') {
    callbacks.current.onSelectWinery(target.slug)
  } else if (target.kind === 'place') {
    callbacks.current.onSelect(target.slug)
  } else if (target.kind === 'tasting-cluster' && target.winery) {
    // One winery's wines: its panel lists them, and rates them where they are.
    callbacks.current.onSelectWinery(target.winery)
  } else if (target.kind === 'tasting-cluster' && target.slug) {
    // The journal is the right destination rather than a new panel: it already
    // lists tastings, already filters by place, and already knows how to say
    // "nothing matches these filters".
    callbacks.current.onSelectCluster(target.slug)
  } else {
    // A winery chip, or the reader's tastings in several places at once.
    //
    // Easing rather than flying: the reader asked to look closer at a cell they
    // can see, not to be moved somewhere else. The next `moveend` is what asks
    // the server to break the cell apart.
    //
    // It eases to the middle of what the chrome leaves uncovered. With a sheet
    // open on a phone the middle of the canvas is under the sheet, and a cell
    // that slid out of sight when it was tapped reads as a tap that did
    // nothing. No `offset` at all when nothing is covered off-centre.
    const [dx, dy] = visibleOffset(callbacks.current.insets())

    map.easeTo({
      center: target.center,
      zoom: map.getZoom() + WINERY_CLUSTER_ZOOM_STEP,
      ...(dx !== 0 || dy !== 0 ? { offset: [dx, dy] as [number, number] } : {}),
    })
  }
}

// Every layer a pointer can be "on" — and a click can land on, which asks the
// same question — of those that exist right now. A layer id
// the style does not have makes `queryRenderedFeatures` fire an `error` and
// return nothing, so the list is filtered rather than assumed: the region tiers
// are absent until their features arrive, and everything is absent for a moment
// after a style swap.
function hoverLayerIds(map: Map, tiers: number[]) {
  return [
    TASTING_WINERY_LAYER_ID,
    TASTING_WINERY_BADGE_LAYER_ID,
    TASTING_POINT_LAYER_ID,
    TASTING_CLUSTER_LAYER_ID,
    WISHLIST_POINT_LAYER_ID,
    ...wineryInteractiveLayerIds(),
    ...regionInteractiveLayerIds(tiers),
    ATLAS_FILL_LAYER_ID,
  ].filter((id) => map.getLayer(id))
}

// The layers whose features are MARKS — a dot, a chip, a pin — rather than
// ground. A mark is small, and a pointer or a thumb that lands beside one meant
// it (`hitsAt`).
const MARK_LAYER_IDS = [
  TASTING_WINERY_LAYER_ID,
  TASTING_WINERY_BADGE_LAYER_ID,
  TASTING_POINT_LAYER_ID,
  TASTING_CLUSTER_LAYER_ID,
  WISHLIST_POINT_LAYER_ID,
  ...wineryInteractiveLayerIds(),
]

/**
 * What is under a pointer, and — when no mark is — the one mark nearest to it
 * within `reach` pixels, put first.
 *
 * A winery dot is about eleven pixels across with its ring, and it sits on an
 * appellation, which is clickable too. So a press four pixels off a dot opened
 * the PLACE under it: a different page, with the same pointer cursor on both,
 * and nothing to say which the reader was about to get. A target that small is
 * also under any guideline's minimum. With a reach of eight pixels a dot
 * answers within about a 24 px circle, and a thumb gets more
 * (`MARK_REACH_COARSE_PX`).
 *
 * EXACT FIRST. A pointer that is on a mark is on that mark, by the same query
 * and the same order as before, so nothing that was right changes. Only when
 * it is on none is the box asked, and then the nearest centre wins: one dot
 * among several beside the pointer is the one the pointer is closest to.
 *
 * That second query is the cost — one more `queryRenderedFeatures`, over the
 * mark layers only and a box a few pixels square, on a pointer move that is
 * not on a mark. Measured in the browser over 2,000 pointer positions on the
 * Santa Ynez view at zoom 10.2 (149 marks drawn, 62 layers in the style): the
 * box is 0.018 ms a call at eight pixels and 0.019 at fourteen, beside the
 * first query's 0.025 — so a pointer move that is on no mark costs about
 * 0.043 ms where it cost 0.025, and one that is on a mark costs what it did.
 */
function hitsAt(map: Map, point: MapMouseEvent['point'], tiers: number[], reach: number): MapGeoJSONFeature[] {
  const layers = hoverLayerIds(map, tiers)
  if (layers.length === 0) return []

  const under = map.queryRenderedFeatures(point, { layers })
  if (reach <= 0 || under.some((hit) => MARK_LAYER_IDS.includes(hit.layer.id))) return under

  const marks = layers.filter((id) => MARK_LAYER_IDS.includes(id))
  if (marks.length === 0) return under

  const near = map.queryRenderedFeatures(
    [
      [point.x - reach, point.y - reach],
      [point.x + reach, point.y + reach],
    ],
    { layers: marks },
  )
  if (near.length === 0) return under

  let nearest = near[0]
  let least = Infinity

  for (const hit of near) {
    if (hit.geometry.type !== 'Point') continue

    const at = map.project(hit.geometry.coordinates as [number, number])
    const apart = Math.hypot(at.x - point.x, at.y - point.y)
    if (apart >= least) continue

    nearest = hit
    least = apart
  }

  return [nearest, ...under]
}

const named = (value: unknown) => (typeof value === 'string' && value !== '' ? value : null)

// The winery chips the reader's own marks keep clear of: the layer's, while it
// is showing chips and is switched on.
function chipsOf(draw: { wineryLayer: WineryLayer | null; layerVisibility: LayerVisibility }) {
  return draw.layerVisibility.wineries ? wineryObstacles(draw.wineryLayer) : []
}

// Where the map has lettered a place, for a mark that hangs under its name:
// the appellations' own anchors, while the appellations are switched on.
const noNames: NameAnchor = () => null

function namesOn(map: Map, draw: { layerVisibility: LayerVisibility }): NameAnchor {
  return draw.layerVisibility.regions ? (slug) => regionNameAt(map, slug) : noNames
}

interface MarkInputs {
  tastingLayer: JournalLayer
  wineryLayer: WineryLayer | null
  wineryMarkZoom?: number
  layerVisibility: LayerVisibility
}

// The reader's counted marks, worked out again for the map as it stands.
function replaceMarks(map: Map, draw: MarkInputs) {
  placeJournalMarks(map, draw.tastingLayer, chipsOf(draw), namesOn(map, draw), draw.wineryMarkZoom)
}

/**
 * Which of the things under the pointer it is on, in one fixed order: the
 * reader's mark on a winery, a tasting pin, a wishlist pin, a winery, the
 * deepest appellation, the country.
 *
 * That is top of the stack first, which is the order a reader sees them in. The
 * appellations go through `resolveFeature`, the same rule a click uses
 * (`clickTarget`), so the name under the pointer is always the place a click
 * would open.
 *
 * A cluster — of tastings or of wineries — wins its place in the order and is
 * named by what it counts ("311 wineries", or the place a reader's tastings
 * are in): it stands for many things, so it names none of them, and pointing at
 * a number with nothing to say about it read as a mark that was not listening.
 * A wishlist pin is named but answers no click of its own, so whether the
 * cursor is a pointer there depends on what is beneath.
 *
 * Every target also carries the tooltip's second line as far as the feature
 * alone can say it (`detail`); what only the map can add is `aboutHover`.
 */
export function hoverTarget(hits: readonly MapGeoJSONFeature[]): HoverTarget | null {
  const on = (...layerIds: string[]) => hits.find((hit) => layerIds.includes(hit.layer.id))

  // The reader's mark on a winery says the winery and how many of their wines
  // are from it; under its badge on a neighbour's dot, it is the neighbour.
  const marked = wineryMarkHit(hits)
  if (marked?.neighbour) return { ...addressed(marked.neighbour, WINERY_SOURCE_ID), detail: 'Winery' }
  if (marked) {
    const { label, detail } = marked.mark.properties ?? {}

    return { name: named(label), clickable: true, detail: named(detail) }
  }

  const tasting = on(TASTING_POINT_LAYER_ID, TASTING_CLUSTER_LAYER_ID)
  if (tasting) {
    return { name: named(tasting.properties?.label), clickable: true, detail: named(tasting.properties?.detail) }
  }

  const wish = on(WISHLIST_POINT_LAYER_ID)
  if (wish) {
    return {
      name: named(wish.properties?.label),
      clickable: hits.some((hit) => hit.layer.id !== WISHLIST_POINT_LAYER_ID),
      detail: 'On your wishlist',
    }
  }

  const winery = on(WINERY_POINT_LAYER_ID)
  if (winery) return { ...addressed(winery, WINERY_SOURCE_ID), detail: 'Winery' }

  // A chip stands for many producers and is none of them, so it names how
  // many — which is the one thing it has to say, and what a click will open up.
  const chip = on(WINERY_CLUSTER_LAYER_ID)
  if (chip) {
    const count = Number(chip.properties?.count)

    return Number.isFinite(count) && count > 0
      ? {
          name: `${count.toLocaleString('en-US')} ${count === 1 ? 'winery' : 'wineries'}`,
          clickable: true,
          detail: 'Click to look closer',
        }
      : { name: null, clickable: true }
  }

  const region = resolveFeature(hits.filter((hit) => hit.layer.id.startsWith(`${REGION_FILL_LAYER_PREFIX}-`)))
  if (region) return { ...addressed(region, REGION_SOURCE_ID), detail: named(region.properties?.kind), of: 'region' }

  const country = on(ATLAS_FILL_LAYER_ID)
  if (country) {
    return {
      ...addressed(country, ATLAS_SOURCE_ID),
      detail: COUNTRY_HOVER_DETAIL[String(country.properties?.coverage)] ?? null,
      of: 'country',
    }
  }

  return null
}

// What the atlas holds for a country, which is the one thing its tint says and
// a reader pointing at it is asking.
const COUNTRY_HOVER_DETAIL: Record<string, string> = {
  [COVERAGE_DRAWN]: 'Country · appellations drawn',
  [COVERAGE_NAMED]: 'Country · appellations listed, no boundaries published',
  [COVERAGE_NONE]: 'Country · no appellations yet',
}

// The slug is the feature id on all three sources (`promoteId: 'slug'`), so it
// is read from the properties rather than from `feature.id`. A winery cluster
// carries the empty slug and is therefore named nothing and highlighted nowhere.
function addressed(feature: MapGeoJSONFeature, source: string): HoverTarget {
  const id = named(feature.properties?.slug)

  return { name: named(feature.properties?.name), clickable: true, ...(id ? { source, id } : {}) }
}

/**
 * What the pointer was last on, and the four things that follow from it: the
 * cursor, the `hover` feature-state, what a click would open, and the name
 * reported to the shell.
 *
 * Each is written only when it changes. The feature-state is what the paint
 * expressions read — an appellation's brighter edge, a stronger country, a
 * ringed dot —
 * and moving it is one repaint with no tile reloaded. The name is a state
 * update on the whole shell, so a pointer crossing one appellation reports it
 * once, not once per pixel.
 */
function hoverState(map: Map, callbacks: CallbackRef, draw: RefObject<HoverInputs>) {
  let current: HoverTarget | null = null
  let reported: string | null = null

  return {
    show(target: HoverTarget | null) {
      const cursor = target?.clickable ? 'pointer' : ''
      const canvas = map.getCanvas()
      if (canvas.style.cursor !== cursor) canvas.style.cursor = cursor

      if (current?.source !== target?.source || current?.id !== target?.id) {
        // The source can be gone — a toggle, a swap — and addressing a missing
        // source is an `error` event.
        if (current?.source && current.id && map.getSource(current.source)) {
          map.removeFeatureState({ source: current.source, id: current.id }, 'hover')
        }
        if (target?.source && target.id) {
          map.setFeatureState({ source: target.source, id: target.id }, { hover: true })
        }

        // The same change, said to the shell: this is the thing a click here
        // would open. Inside this branch on purpose — it is the one place that
        // already runs once per feature rather than once per pointer move.
        callbacks.current.onIntent(intentOf(target))
      }

      current = target

      // Compared as the two lines the feature alone gives, so nothing below
      // this — the parent lookup, the count — runs on a pointer move that is
      // still on the same thing.
      const name = target?.name ?? null
      const said = name === null ? null : `${name}\n${target?.detail ?? ''}`
      if (said === reported) return

      reported = said
      if (name === null || !target) callbacks.current.onHover(null)
      else callbacks.current.onHover(name, aboutHover(map, target, draw.current))
    },

    // After a style swap there is no feature-state left to take off anything.
    // The reported name is kept: the pointer has not moved.
    forget() {
      current = null
    },
  }
}

interface HoverInputs {
  wineryLayer: WineryLayer | null
  layerVisibility: LayerVisibility
}

/**
 * The tooltip's second line and where to keep off, for what the pointer has
 * just moved onto. Run when that CHANGES, never per pointer move.
 *
 * For an appellation it adds what only the map knows:
 *
 * - WHOSE it is — "AVA in Santa Ynez Valley" — from the shapes the map is
 *   holding (`regionSetting`);
 * - HOW MANY PRODUCERS are inside it, when that can be said truthfully: the
 *   winery layer is showing dots, was not cut short, and its frame holds the
 *   whole of the appellation, so every dot inside the line is in hand and
 *   counted. Otherwise nothing is said — a count of the dots that happen to be
 *   on screen would be read as the appellation's. Nor is a zero: nothing drawn
 *   is often the atlas not having looked.
 * - and where its own name is set, so the tooltip does not cover it.
 */
function aboutHover(map: Map, target: HoverTarget, draw: HoverInputs): HoverAbout {
  const detail = target.detail ?? null
  if (target.of !== 'region' || !target.id) return { detail, area: false, anchor: null }

  const setting = regionSetting(map, target.id)
  if (!setting) return { detail, area: true, anchor: null }

  const parts = [setting.parent ? `${detail ?? 'Appellation'} in ${setting.parent}` : detail]
  const layer = draw.wineryLayer
  const { shape } = setting

  if (shape && setting.whole && layer && layer.mode === 'points' && !layer.truncated && draw.layerVisibility.wineries) {
    const { bounds } = layer
    const inFrame =
      bounds.min_lng <= shape.box.west &&
      bounds.max_lng >= shape.box.east &&
      bounds.min_lat <= shape.box.south &&
      bounds.max_lat >= shape.box.north

    if (inFrame) {
      let inside = 0
      for (const winery of layer.wineries) if (shapeHolds(shape, winery.lng, winery.lat)) inside++

      if (inside > 0) parts.push(`${inside.toLocaleString('en-US')} ${inside === 1 ? 'winery' : 'wineries'}`)
    }
  }

  const at = map.project(setting.at as [number, number])
  const box = map.getContainer().getBoundingClientRect()

  return {
    detail: parts.filter(Boolean).join(' · ') || null,
    area: true,
    anchor: [box.left + at.x, box.top + at.y],
  }
}

// A feature that carries its slug is one a click opens: a winery dot on the
// winery source, an appellation or a country on the other two.
function intentOf(target: HoverTarget | null): HoverIntent | null {
  if (!target?.source || !target.id) return null

  return { type: target.source === WINERY_SOURCE_ID ? 'winery' : 'place', slug: target.id }
}

function applyVisibility(map: Map, tiers: number[], visibility: LayerVisibility) {
  for (const id of [ATLAS_FILL_LAYER_ID, ATLAS_RIBBON_LAYER_ID, ATLAS_NAMED_LINE_LAYER_ID]) {
    if (!map.getLayer(id)) continue
    map.setLayoutProperty(id, 'visibility', visibility.countries ? 'visible' : 'none')
  }

  if (tiers.length > 0) setRegionVisibility(map, tiers, visibility.regions)
  setWineryVisibility(map, visibility.wineries)
  setJournalVisibility(map, visibility.tastings, visibility.wishlist)
}

interface SelectionInputs {
  worldLayer: AtlasLayer | null
  regionLayer: DetailLayer | null
  layerVisibility: LayerVisibility
  selectedSlug: string | null
  selectedOutline: Geometry | null
}

// The feature the selection is a copy of: the appellation with that slug if the
// map holds one, else the country. An appellation is looked for first because a
// slug is unique across both and the region layer is the finer of the two.
//
// Nothing when its layer is hidden. The selection is drawn from its own source,
// so without this a reader who turned Regions off would be left with one amber
// outline around a region that is no longer there.
function selectedFeature({ worldLayer, regionLayer, layerVisibility, selectedSlug }: SelectionInputs) {
  if (!selectedSlug) return null

  const region = regionLayer?.features.find((feature) => feature.properties.slug === selectedSlug)
  if (region) return layerVisibility.regions ? (region as SelectionFeature) : null

  // Absent until the deferred country layer lands. The selection effect keys
  // on the layer, so a country opened by deep link is outlined when it does.
  const country = worldLayer?.features.find((feature) => feature.properties.slug === selectedSlug)
  if (country) return layerVisibility.countries ? (country as SelectionFeature) : null

  return null
}

// The two sources whose paint reads an appellation's `selected` state: its
// shape, whose claret ribbon and hairline step aside for the amber ones, and
// its name, which takes the selection's ink.
const SELECTABLE_SOURCES = [REGION_SOURCE_ID, REGION_LABEL_SOURCE_ID]

/**
 * Draws the place the open panel describes, and only if it is not already drawn.
 *
 * One `setData` of two features on a source nothing else reads — the place, and
 * the world with the place cut out of it — plus one feature-state on the
 * appellation's shape and one on its name. No paint property is set: a
 * data-driven `setPaintProperty` re-cuts every tile of the source it touches
 * (region_layer.ts has the numbers).
 *
 * `selectedOutline` replaces the geometry and keeps the properties, so a
 * country's finer outline is still treated as a country. It is the finest shape
 * the map is handed for the place, and the veil and the amber edge are both cut
 * from it; the claret edge it replaces is drawn from whatever tier the camera's
 * zoom was served, which is why that one is switched off rather than covered.
 */
function showSelection(map: Map, inputs: SelectionInputs, shown: RefObject<ShownSelection>) {
  const base = selectedFeature(inputs)
  const outline = base ? inputs.selectedOutline : null
  // Only an appellation is in the two region sources. A country's edge and name
  // are the basemap's own, which are not ours to restyle.
  const label = base && base.properties.kind !== COUNTRY_KIND ? inputs.selectedSlug : null
  const previous = shown.current

  if (base !== previous.base || outline !== previous.outline) {
    setSelection(map, base && outline ? { ...base, geometry: outline } : base)
  }

  // The region sources exist only once a region has arrived — both are added in
  // one call, so one stands for the other — and a feature-state on a source the
  // style does not have is an `error` event. So this is retried, not assumed:
  // `label` is recorded as shown only when it was set.
  let labelled = previous.label

  if (labelled !== label && map.getSource(REGION_LABEL_SOURCE_ID)) {
    for (const source of SELECTABLE_SOURCES) {
      if (labelled) map.removeFeatureState({ source, id: labelled }, 'selected')
      if (label) map.setFeatureState({ source, id: label }, { selected: true })
    }
    labelled = label
  }

  shown.current = { base, outline, label: labelled }
}

const pair = (at: LngLatLike): [number, number] => {
  if (Array.isArray(at)) return [at[0], at[1]]

  return ['lng' in at ? at.lng : at.lon, at.lat]
}

function positionOf(map: Map): CameraPosition {
  return { center: pair(map.getCenter()), zoom: map.getZoom() }
}

function canvasSize(map: Map): Size {
  const node = map.getContainer()

  return { width: node.clientWidth, height: node.clientHeight }
}

// Vestibular disorders are why the setting exists, and a camera that swoops
// across a continent is the thing it asks not to see. MapLibre honours it on
// its own for any move not marked `essential`; it is read here too so the
// decision is this app's and is not one library default away from changing.
function stillCamera() {
  return typeof window !== 'undefined' && (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false)
}

/**
 * Moves a map that is already up to what a panel now describes.
 *
 * Every move here says how long it takes and where the chrome is. The duration
 * is scaled by how far the camera has to go (`flightMs`), because MapLibre's
 * default speed made a hop next door take three seconds; the insets are why
 * the place lands beside the panel and above the sheet instead of under them.
 *
 * And every move that happens says where it will land (`announce`), before it
 * starts: the landing camera is known the moment the move is decided, and the
 * shell uses it to fetch the destination's appellations during the flight
 * rather than after it.
 *
 * A MOVE THAT WOULD LAND WHERE THE MAP ALREADY IS DOES NOT HAPPEN (`there`,
 * below; `SAME_CAMERA_PX` has the measurement and the owner's ruling). It
 * announces nothing either: the frame it would land in is the one the map last
 * reported, and no `moveend` follows a move that was never made.
 */
function moveCamera(map: Map, camera: PlaceCamera, insets: Insets, tell: (viewport: MapViewport) => void) {
  // The relief is told too, and first: it is as strong as the images under the
  // ground it is over allow, and a flight out of the open ocean would otherwise
  // cross its last zooms with the relief faded for where it left (relief.ts).
  const announce = (viewport: MapViewport) => {
    settleRelief(map, viewport)
    tell(viewport)
  }
  const size = canvasSize(map)
  const from = positionOf(map)
  const still = stillCamera()
  const ms = (to: CameraPosition) => (still ? 0 : flightMs(from, to, size))

  // Whether the map is already at a landing camera. Only a map at rest,
  // north-up and flat can be:
  //
  // - One in motion is on its way somewhere else, and this move is what stops
  //   it. That the camera is passing through the landing as the question is
  //   asked says nothing about where it will come to rest.
  // - One the reader has turned is turned back to north by a fit, which is a
  //   move a reader sees even when the centre and the zoom hold.
  // - And a landing is worked out for a north-up, flat camera (lib/camera.ts),
  //   so on a turned or tilted map it is not known well enough to call the
  //   move nothing. That map flies, as it always did.
  const settled = !map.isMoving() && map.getBearing() === 0 && map.getPitch() === 0
  const there = (landing: CameraPosition) => settled && sameCamera(from, landing)

  const bounds = boundsToFit(camera)
  if (bounds) {
    const padding = fitPadding(insets, size)
    // Asked for only to learn how far the fit will travel; `fitBounds` works
    // the same camera out again for itself. When it has no answer — a canvas
    // with no size — `fitBounds` will not move either, and the longest
    // duration is as good as any.
    const fitted = map.cameraForBounds(bounds as LngLatBoundsLike, { padding })
    const landing =
      fitted?.center && fitted.zoom !== undefined ? { center: pair(fitted.center), zoom: fitted.zoom } : null
    const duration = landing ? ms(landing) : still ? 0 : FLIGHT_MAX_MS

    // The breadcrumb from a producer back to its appellation: the box the map
    // was built fitted to, with the padding it was built with.
    if (landing && there(landing)) return

    if (landing) announce(frameAt(landing, size))
    map.fitBounds(bounds as LngLatBoundsLike, { padding, duration })
    return
  }

  // A place with only a representative point flies to it; a place with neither
  // opens its panel without moving the camera at all. `camera.zoom` is set when
  // the point is an ancestor's, and for a winery.
  if (!camera.point) return

  const offset = visibleOffset(insets)

  if (camera.visibleFrom !== undefined) {
    // Asked of the map rather than worked out, because the reader may have
    // rotated it: `project` knows about a bearing and the arithmetic in
    // lib/camera.ts does not.
    const at = map.project(camera.point)
    const drawn = from.zoom >= camera.visibleFrom

    // Already a dot in plain view: the reader keeps the view they chose.
    if (drawn && inVisiblePart(at, size, insets)) return

    // A dot they could see, now under the panel that opened for it. Slid into
    // view at the zoom they were at — they picked this dot out at this zoom,
    // and zooming in on them would lose the neighbours they picked it from.
    if (drawn && at.x >= 0 && at.x <= size.width && at.y >= 0 && at.y <= size.height) {
      announce(frameAt({ center: centreFor(camera.point, from.zoom, offset), zoom: from.zoom }, size))
      map.easeTo({ center: camera.point, offset, duration: still ? 0 : FLIGHT_MIN_MS })
      return
    }
  }

  const zoom = camera.zoom ?? PLACE_FLY_ZOOM
  // The point is drawn at `offset` from the middle of the canvas, so the
  // camera's own centre — and the frame round it — is that far the other way.
  const landing = { center: centreFor(camera.point, zoom, offset), zoom }

  // The same return, to a place that has only a point.
  if (there(landing)) return

  announce(frameAt(landing, size))
  map.flyTo({ center: camera.point, zoom, offset, duration: ms({ center: camera.point, zoom }) })
}
